/**
 * Analytics consent, read the same way everywhere.
 *
 * PECR (UK) and the ePrivacy Directive (EU) require consent before anything
 * that is not strictly necessary is stored on the visitor's device, and that
 * includes localStorage and sessionStorage, not only cookies. Our visitor id,
 * session id and first/last-touch attribution are analytics, so they stay in
 * memory until the visitor allows analytics.
 *
 * The rule mirrors Consent Mode v2: an explicit choice (a consent 'update' in
 * the dataLayer, restored on every page load by index.html) always wins;
 * without one, Europe (by time zone, set in index.html) starts denied and
 * everywhere else starts granted.
 */

export const CONSENT_EVENT = "ec:consent-updated";

export function hasAnalyticsConsent(): boolean {
  try {
    const dataLayer = (window as unknown as { dataLayer?: unknown[] }).dataLayer || [];
    for (let i = dataLayer.length - 1; i >= 0; i--) {
      const entry = dataLayer[i] as { [k: number]: unknown } | undefined;
      if (entry?.[0] === "consent" && entry?.[1] === "update") {
        const state = (entry?.[2] as { analytics_storage?: string } | undefined)?.analytics_storage;
        if (state === "granted") return true;
        if (state === "denied") return false;
      }
    }
    return (window as unknown as { __ecConsentRegion?: string }).__ecConsentRegion !== "eu";
  } catch {
    return false;
  }
}

/** Runs `fn` whenever the visitor changes their choice. Returns an unsubscribe. */
export function onConsentChange(fn: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(CONSENT_EVENT, fn);
  return () => window.removeEventListener(CONSENT_EVENT, fn);
}
